/**
 * What counts as a valid email address and mobile number for a candidate.
 *
 * Shared rather than client-side, because the form and the route both have to agree. A rule written
 * twice is a rule that drifts: the browser accepts an address the server refuses, and somebody
 * spends an afternoon on a form that will not save and gives no reason worth reading.
 */

/** Ten digits, nothing else. Punctuation is stripped before this applies, not rejected. */
export const MOBILE_DIGITS = 10;

/**
 * The dialling codes on offer, India first because that is where this is used.
 *
 * A short list on purpose. Every entry is a country somebody has actually been interviewed from; a
 * full ITU table would be two hundred rows to scroll past to reach +91, and adding a country when
 * one is needed costs one line.
 */
export const DIAL_CODES: readonly { code: string; label: string }[] = [
    { code: '91', label: 'India +91' },
    { code: '1', label: 'USA / Canada +1' },
    { code: '44', label: 'United Kingdom +44' },
    { code: '61', label: 'Australia +61' },
    { code: '65', label: 'Singapore +65' },
    { code: '971', label: 'United Arab Emirates +971' },
    { code: '966', label: 'Saudi Arabia +966' },
    { code: '974', label: 'Qatar +974' },
    { code: '968', label: 'Oman +968' },
    { code: '973', label: 'Bahrain +973' },
    { code: '60', label: 'Malaysia +60' },
    { code: '94', label: 'Sri Lanka +94' },
    { code: '977', label: 'Nepal +977' },
    { code: '880', label: 'Bangladesh +880' },
    { code: '49', label: 'Germany +49' },
    { code: '33', label: 'France +33' },
    { code: '81', label: 'Japan +81' },
    { code: '86', label: 'China +86' }
];

export const DEFAULT_DIAL_CODE = '91';

export const digitsOf = (value: string | null | undefined): string =>
    (value ?? '').replace(/\D/g, '');

/**
 * One @, a sensible local part, and a domain of at most three labels.
 *
 * Assembled from pieces rather than written as one expression, because the useful part is which
 * rule rejected the address - and a single regex doing all of this is unreadable to the next person
 * who has to widen it.
 *
 *   local    letters, digits and . _ % + - ; no dot at either end and never two in a row, so
 *            "priya..sharma@" and ".priya@" are refused
 *   domain   labels of letters, digits and hyphens, no hyphen at either end of a label
 *   dots     at most two in the domain, which is company.com and company.co.in but not a fourth
 *            level below that
 *   tld      at least two letters, so "@company." and "@company.c" are refused
 */
const LOCAL = /^[A-Za-z0-9_%+-]+(?:\.[A-Za-z0-9_%+-]+)*$/;
const LABEL = /^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/;
const TLD = /^[A-Za-z]{2,}$/;
const MAX_DOMAIN_DOTS = 2;
const MAX_EMAIL = 200;

/** The problem with an email address, in words, or null when there is none. */
export function emailError(raw: string): string | null {
    const value = (raw ?? '').trim();

    if (!value) {
        return 'An email address is required.';
    }

    if (/\s/.test(value)) {
        return 'An email address cannot contain spaces.';
    }

    if (value.length > MAX_EMAIL) {
        return 'That email address is too long.';
    }

    // Counted rather than split-and-check, so "a@@b.com" and "a@b@c.com" both give this clear answer
    // instead of a confusing complaint about the domain.
    const ats = value.split('@').length - 1;

    if (ats !== 1) {
        return ats === 0
            ? 'An email address must contain an @.'
            : 'An email address can contain only one @.';
    }

    const [local, domain] = value.split('@');

    if (!LOCAL.test(local)) {
        return 'The part before the @ is not a valid email name.';
    }

    if (local.length > 64) {
        return 'The part before the @ is too long.';
    }

    const labels = domain.split('.');

    if (labels.length < 2) {
        return 'The part after the @ must contain a dot, like company.com.';
    }

    if (labels.length - 1 > MAX_DOMAIN_DOTS) {
        return 'The part after the @ can contain at most two dots.';
    }

    if (!labels.slice(0, -1).every((l) => LABEL.test(l))) {
        return 'The part after the @ is not a valid domain.';
    }

    if (!TLD.test(labels[labels.length - 1]!)) {
        return 'The address must end in a domain like .com, .in or .co.in.';
    }

    return null;
}

/** The problem with a mobile number, in words, or null when there is none. */
export function mobileError(raw: string): string | null {
    const digits = digitsOf(raw);

    if (!digits) {
        return 'A mobile number is required.';
    }

    return digits.length === MOBILE_DIGITS
        ? null
        : `A mobile number must be exactly ${MOBILE_DIGITS} digits.`;
}

/** What goes in the database: "+91 9876543210". One field, parsed back by splitMobile. */
export const joinMobile = (dialCode: string, mobile: string): string =>
    `+${digitsOf(dialCode)} ${digitsOf(mobile)}`;

/**
 * A stored number back into a dialling code and a national number.
 *
 * Numbers saved before this screen had a dropdown are a plain string of digits with no separator, so
 * a known code is looked for at the front and whatever is left is treated as the national number.
 * What will not parse is handed back whole, for whoever opens the record to fix, rather than
 * silently dropped.
 */
export function splitMobile(stored: string | null | undefined): {
    dialCode: string;
    mobile: string;
} {
    const digits = digitsOf(stored);

    if (!digits || digits.length === MOBILE_DIGITS) {
        return { dialCode: DEFAULT_DIAL_CODE, mobile: digits };
    }

    // Longest code first, so +1 does not claim a number that begins +91.
    const match = [...DIAL_CODES]
        .sort((a, b) => b.code.length - a.code.length)
        .find((c) => digits.startsWith(c.code)
            && digits.length - c.code.length === MOBILE_DIGITS);

    return match
        ? { dialCode: match.code, mobile: digits.slice(match.code.length) }
        : { dialCode: DEFAULT_DIAL_CODE, mobile: digits };
}
